import type { Session } from '@/types';

const SNAP_MINUTES = 15;

const wallTimeParts = (date: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone,
    year: 'numeric',
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return {
    day: value('day'),
    hour: value('hour'),
    minute: value('minute'),
    month: value('month'),
    second: value('second'),
    year: value('year'),
  };
};

export const dropStartTime = (
  dateKey: string,
  minutesAfterMidnight: number,
  timeZone: string,
): string => {
  const [year = Number.NaN, month = Number.NaN, day = Number.NaN] = dateKey
    .split('-')
    .map(Number);
  if (![year, month, day].every(Number.isFinite)) {
    throw new Error(`Invalid calendar date: ${dateKey}`);
  }
  const snappedMinutes = Math.max(
    0,
    Math.min(
      24 * 60 - SNAP_MINUTES,
      Math.round(minutesAfterMidnight / SNAP_MINUTES) * SNAP_MINUTES,
    ),
  );
  const target = {
    day,
    hour: Math.floor(snappedMinutes / 60),
    minute: snappedMinutes % 60,
    month,
    second: 0,
    year,
  };
  const targetAsUtc = Date.UTC(
    target.year,
    target.month - 1,
    target.day,
    target.hour,
    target.minute,
  );
  let candidate = targetAsUtc;

  for (let attempt = 0; attempt < 4; attempt += 1) {
    const actual = wallTimeParts(new Date(candidate), timeZone);
    const actualAsUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second,
    );
    const adjustment = targetAsUtc - actualAsUtc;
    if (adjustment === 0) break;
    candidate += adjustment;
  }

  const resolved = wallTimeParts(new Date(candidate), timeZone);
  if (
    resolved.year !== target.year ||
    resolved.month !== target.month ||
    resolved.day !== target.day ||
    resolved.hour !== target.hour ||
    resolved.minute !== target.minute
  ) {
    throw new Error(`The selected time does not exist in ${timeZone}`);
  }
  return new Date(candidate).toISOString();
};

export const isAdminDraggableSession = (session: Session): boolean =>
  !session.isChemistryCall &&
  session.coachRescheduleAvailable === true &&
  session.bookingSource === 'moments';
